import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/api";
import axiosPrivate from "../../services/axios/axiosPrivate";

/* ================= CREATE ================= */
export const createTicket = createAsyncThunk(
  "tickets/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(
        api.support.createTicket,
        payload
      );

      if (res.data?.success === false) {
        return rejectWithValue(res.data.reason);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Create failed"
      );
    }
  }
);

/* ================= FETCH MY ================= */
export const fetchMyTickets = createAsyncThunk(
  "support/fetchMyTickets",
  async (params, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        api.support.getMyTickets(),
        { params }
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Fetch failed"
      );
    }
  }
);

/* ================= FETCH ALL (HR) ================= */
export const fetchAllTickets = createAsyncThunk(
  "support/fetchAllTickets",
  async (params, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        api.support.getAllTickets(),
        { params }
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Fetch failed"
      );
    }
  }
);

/* ================= FETCH BY ID ================= */
export const fetchTicketById = createAsyncThunk(
  "support/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        api.support.getTicketById(id)
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Fetch detail failed"
      );
    }
  }
);

/* ================= ATTACH DOC ================= */
export const attachTicketDoc = createAsyncThunk(
  "support/attachDoc",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(
        api.support.attachDoc(id), formData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Upload failed"
      );
    }
  }
);

/* ================= GET DOC ================= */
export const getTicketDoc = createAsyncThunk(
  "support/getDoc",
  async ({ ticketId, fileType, fileName }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(api.support.getDoc(ticketId),
        {
          params: { fileType, fileName },
          responseType: 'blob'
        }
      );
      // Return only the binary data
      return res.data;
    } catch (err) {
      if (err.response?.data instanceof Blob && err.response.data.type === 'application/json') {
        const errorText = await err.response.data.text();
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.reason && errorJson.reason.includes('{')) {
            const nestedError = JSON.parse(errorJson.reason.split('type: ')[1] || errorJson.reason.split(': ')[1]);
            return rejectWithValue(nestedError.error.message);
          }
          return rejectWithValue(errorJson.reason || "File not found");
        } catch (e) {
          return rejectWithValue("File not found on server");
        }
      }
      return rejectWithValue("Download failed");
    }
  }
);

/* ================= FETCH ALL Categories ================= */
export const fetchAllCategories = createAsyncThunk(
  "support/fetchAllCategories",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        api.support.getAllCategories()
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Fetch categories failed"
      );
    }
  }
);

/* ================= ADD COMMENT ================= */
export const addComment = createAsyncThunk(
  "support/addComment",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(
        api.support.addComment(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Add comment failed"
      );
    }
  }
);

/* ================= RE-ASSIGN TICKET ================= */
export const reAssignTicket = createAsyncThunk(
  "support/reAssignTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.reAssignTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Reassignment failed"
      );
    }
  }
);

/* ================= PROCESSING ================= */
export const markTicketProcessing = createAsyncThunk(
  "support/markProcessing",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.markProcessing(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Processing failed"
      );
    }
  }
);

/* ================= APPROVE ================= */
export const approveTicket = createAsyncThunk(
  "support/approveTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.approveTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Approve failed"
      );
    }
  }
);

/* ================= PARTIAL APPROVE ================= */
export const partialApproveTicket = createAsyncThunk(
  "support/partialApproveTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.partialApproveTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Partial approve failed"
      );
    }
  }
);

/* ================= REJECT ================= */
export const rejectTicket = createAsyncThunk(
  "support/rejectTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.rejectTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Reject failed"
      );
    }
  }
);

/* ================= VERIFY ================= */
export const verifyTicket = createAsyncThunk(
  "support/verifyTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.verifyTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Verify failed"
      );
    }
  }
);

/* ================= REWORK ================= */
export const reworkTicket = createAsyncThunk(
  "support/reworkTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.reworkTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Rework failed"
      );
    }
  }
);

/* ================= CLOSE ================= */
export const closeTicket = createAsyncThunk(
  "support/closeTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.closeTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Close failed"
      );
    }
  }
);

/* ================= ESCALATE ================= */
export const escalateTicket = createAsyncThunk(
  "support/escalateTicket",
  async ({ ticketId, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        api.support.escalateTicket(ticketId),
        payload
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Escalate failed"
      );
    }
  }
);

/* ================= FETCH ASSIGNMENT USERS ================= */
export const fetchAssignmentUsers = createAsyncThunk(
  "support/fetchAssignmentUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        api.support.getAssignmentUsers()
      );

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        "Failed to fetch assignment users"
      );
    }
  }
);

/* ================= SLICE ================= */
const supportSlice = createSlice({
  name: "support",

  initialState: {
    tickets: [],
    total: 0,
    selectedTicket: null,
    categories: [],
    loading: false,
    error: null,
    createLoading: false,
    createSuccess: false,
    createError: null,
    docs: [],
    docLoading: false,
    docError: null,
    commentLoading: false,
    assignmentUsers: [],
    reassignLoading: false,
    reassignError: null,
    actionLoading: false,
    actionError: null,
  },

  reducers: {
    clearCreateState: (state) => {
      state.createSuccess = false;
      state.createError = null;
    },

    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ================= CREATE ================= */

      .addCase(createTicket.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.createError = null;
      })

      .addCase(createTicket.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;

        state.tickets.unshift(action.payload);
      })

      .addCase(createTicket.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })

      /* ================= FETCH MY ================= */

      .addCase(fetchMyTickets.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchMyTickets.fulfilled, (state, action) => {
        state.loading = false;

        state.tickets = action.payload?.gridData || [];

        state.total = action.payload?.tsize || 0;
      })

      .addCase(fetchMyTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= FETCH ALL ================= */

      .addCase(fetchAllTickets.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchAllTickets.fulfilled, (state, action) => {
        state.loading = false;

        state.tickets = action.payload?.gridData || [];

        state.total = action.payload?.tsize || 0;
      })

      .addCase(fetchAllTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= FETCH BY ID ================= */

      .addCase(fetchTicketById.pending, (state) => {
        state.loading = true;
        state.selectedTicket = null;
      })

      .addCase(fetchTicketById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTicket = action.payload;
      })

      .addCase(fetchTicketById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= ATTACH DOC ================= */

      .addCase(attachTicketDoc.pending, (state) => {
        state.docLoading = true;
      })

      .addCase(attachTicketDoc.fulfilled, (state, action) => {
        state.docLoading = false;
        state.docs = action.payload;
      })

      .addCase(attachTicketDoc.rejected, (state, action) => {
        state.docLoading = false;
        state.docError = action.payload;
      })

      /* ================= GET DOC ================= */

      .addCase(getTicketDoc.pending, (state) => {
        state.docLoading = true;
      })

      .addCase(getTicketDoc.fulfilled, (state, action) => {
        state.docLoading = false;
        state.docs = action.payload;
      })

      .addCase(getTicketDoc.rejected, (state, action) => {
        state.docLoading = false;
        state.docError = action.payload;
      })

      /* ================= FETCH CATEGORIES ================= */

      .addCase(fetchAllCategories.fulfilled, (state, action) => {
        state.categories = action.payload || [];
      })

      /* ================= ADD COMMENT ================= */

      .addCase(addComment.pending, (state) => {
        state.commentLoading = true;
      })

      .addCase(addComment.fulfilled, (state, action) => {
        state.commentLoading = false;

        if (state.selectedTicket) {
          if (!state.selectedTicket.comments) {
            state.selectedTicket.comments = [];
          }

          state.selectedTicket.comments.push(action.payload);
        }
      })

      .addCase(addComment.rejected, (state) => {
        state.commentLoading = false;
      })

      /* ================= RE-ASSIGN TICKET ================= */

      .addCase(reAssignTicket.pending, (state) => {
        state.reassignLoading = true;
        state.reassignError = null;
      })

      .addCase(reAssignTicket.fulfilled, (state, action) => {
        state.reassignLoading = false;

        if (state.selectedTicket) {
          state.selectedTicket.assignedTo =
            action.payload?.assignedTo;

          state.selectedTicket.assignedToName =
            action.payload?.assignedToName;
        }
      })

      .addCase(reAssignTicket.rejected, (state, action) => {
        state.reassignLoading = false;
        state.reassignError = action.payload;
      })

      /* ================= FETCH ASSIGNMENT USERS ================= */

      .addCase(
        fetchAssignmentUsers.fulfilled,
        (state, action) => {
          state.assignmentUsers =
            action.payload || [];
        }
      )
      /* ================= COMMON ACTIONS ================= */

      .addMatcher(
        (action) =>
          action.type.startsWith("support/") &&
          action.type.endsWith("/pending") &&
          [
            "support/markProcessing/pending",
            "support/approveTicket/pending",
            "support/partialApproveTicket/pending",
            "support/rejectTicket/pending",
            "support/verifyTicket/pending",
            "support/reworkTicket/pending",
            "support/closeTicket/pending",
            "support/escalateTicket/pending",
          ].includes(action.type),

        (state) => {
          state.actionLoading = true;
          state.actionError = null;
        }
      )

      .addMatcher(
        (action) =>
          action.type.startsWith("support/") &&
          action.type.endsWith("/fulfilled") &&
          [
            "support/markProcessing/fulfilled",
            "support/approveTicket/fulfilled",
            "support/partialApproveTicket/fulfilled",
            "support/rejectTicket/fulfilled",
            "support/verifyTicket/fulfilled",
            "support/reworkTicket/fulfilled",
            "support/closeTicket/fulfilled",
            "support/escalateTicket/fulfilled",
          ].includes(action.type),

        (state, action) => {
          state.actionLoading = false;

          if (state.selectedTicket) {
            state.selectedTicket.status =
              action.payload?.status ||
              state.selectedTicket.status;
          }
        }
      )

      .addMatcher(
        (action) =>
          action.type.startsWith("support/") &&
          action.type.endsWith("/rejected") &&
          [
            "support/markProcessing/rejected",
            "support/approveTicket/rejected",
            "support/partialApproveTicket/rejected",
            "support/rejectTicket/rejected",
            "support/verifyTicket/rejected",
            "support/reworkTicket/rejected",
            "support/closeTicket/rejected",
            "support/escalateTicket/rejected",
          ].includes(action.type),

        (state, action) => {
          state.actionLoading = false;
          state.actionError = action.payload;
        }
      )
  },
});

export const {
  clearCreateState,
  clearSelectedTicket,
} = supportSlice.actions;

export default supportSlice.reducer;